import { database } from '../firebaseConfig';
import { ref as dbRef, get, set, remove } from "firebase/database";
import { hasPermission, Permissions } from './authService';
import { auth } from '../firebaseConfig';
import { createUserWithEmailAndPassword, listUsers, fetchSignInMethodsForEmail } from 'firebase/auth';

export const fetchUsers = async (userRole) => {
    if (!hasPermission(userRole, Permissions.ADMIN)) {
        throw new Error('Unauthorized access');
    }

    try {
        const usersRef = dbRef(database, 'users');
        const snapshot = await get(usersRef);
        return snapshot.val() || {};
    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

export const updateUserRole = async (userId, newRole, adminRole) => {
    if (!hasPermission(adminRole, Permissions.ADMIN)) {
        throw new Error('Unauthorized access');
    }

    try {
        const userRef = dbRef(database, `users/${userId}/role`);
        await set(userRef, newRole);
    } catch (error) {
        console.error('Error updating user role:', error);
        throw error;
    }
};

export const deleteUser = async (userId, adminRole) => {
    if (!hasPermission(adminRole, Permissions.ADMIN)) {
        throw new Error('Unauthorized access');
    }

    try {
        const userRef = dbRef(database, `users/${userId}`);
        await remove(userRef);
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};

export const getSystemStats = async (adminRole) => {
    if (!hasPermission(adminRole, Permissions.ADMIN)) {
        throw new Error('Unauthorized access');
    }

    try {
        // Fetch various statistics
        const [users, albums, photos] = await Promise.all([
            get(dbRef(database, 'users')),
            get(dbRef(database, 'gallery/legacy/albums')),
            get(dbRef(database, 'gallery/legacy/photos'))
        ]);

        return {
            totalUsers: Object.keys(users.val() || {}).length,
            totalAlbums: Object.keys(albums.val() || {}).length,
            totalPhotos: Object.keys(photos.val() || {}).length
        };
    } catch (error) {
        console.error('Error fetching system stats:', error);
        throw error;
    }
};

export const createUser = async (userData, adminRole) => {
    if (!hasPermission(adminRole, Permissions.ADMIN)) {
        throw new Error('Unauthorized access');
    }

    try {
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(
            auth,
            userData.email,
            userData.password
        );

        const userId = userCredential.user.uid;
        const timestamp = Date.now();
        
        // Store in users collection for admin access and permissions
        const userRef = dbRef(database, `users/${userId}`);
        await set(userRef, {
            email: userData.email,
            role: userData.role,
            created_at: timestamp
        });

        // Store in gallery/active/users for role-based permissions
        const activeUserRef = dbRef(database, `gallery/active/users/${userId}`);
        await set(activeUserRef, {
            email: userData.email,
            role: userData.role,
            created_at: timestamp
        });

        // Store in gallery/legacy/users for legacy data access
        const legacyUserRef = dbRef(database, `gallery/legacy/users/${userId}`);
        await set(legacyUserRef, {
            email: userData.email,
            role: userData.role,
            created_at: timestamp
        });

        return userCredential.user;
    } catch (error) {
        console.error('Error creating user:', error);
        throw error;
    }
};

export const syncAuthUsersWithDatabase = async (adminRole) => {
    if (adminRole !== 'admin') {
        console.log('Permission check failed for admin role:', adminRole);
        throw new Error('Unauthorized access');
    }

    try {
        console.log('Starting user synchronization...');
        
        // Get all users from Realtime Database
        const dbUsersSnapshot = await get(dbRef(database, 'users'));
        const activeUsersSnapshot = await get(dbRef(database, 'gallery/active/users'));
        const dbUsers = dbUsersSnapshot.val() || {};
        const activeUsers = activeUsersSnapshot.val() || {};
        
        console.log('Current database users:', dbUsers);
        console.log('Current active users:', activeUsers);

        // Sync active users to main users collection if they don't exist
        for (const [userId, userData] of Object.entries(activeUsers)) {
            if (!dbUsers[userId]) {
                await set(dbRef(database, `users/${userId}`), {
                    email: userData.email,
                    role: userData.role || 'user',
                    created_at: userData.created_at || Date.now()
                });
                console.log(`Synced active user ${userData.email} to main collection`);
            }
        }

        console.log('User synchronization completed');
        return true;
    } catch (error) {
        console.error('Error in syncAuthUsersWithDatabase:', error);
        throw error;
    }
}; 